"use client";

import React from "react";
import { ArticleAuthors } from "../../partials/ArticleAuthors/ArticleAuthors";
import { ArticleBody } from "../../partials/ArticleBody/ArticleBody";
import { ArticleHeader } from "../../partials/ArticleHeader/ArticleHeader";
import { TabbedRelatedContent } from "../../partials/TabbedArticles/TabbedArticles";
import { RelatedEntities } from "../../partials/RelatedEntities/RelatedEntities";
import { RelatedMatches } from "../../partials/RelatedMatches/RelatedMatches";
import { ArticleMatchDetail } from "../../partials/ArticleMatchDetail/ArticleMatchDetail";
import { useArticleData } from "../../context/ArticleDataContext";
import { AdSlot } from "@/features/ads/AdSlot";
import { DiscussionWidget } from "@/features/discussion/DiscussionWidget";
import { ChallengeZone } from "@/features/challenge-zone/ChallengeZone";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { ARTICLE_HERO, ARTICLE_RELATED } from "@/features/theme/sectionKeys";

interface ArticleLayoutDefaultProps {
    sectionStyles?: Record<string, BrandedStyle>;
    breadcrumbs?: React.ReactNode;
}

export function ArticleLayoutDefault({ sectionStyles, breadcrumbs }: ArticleLayoutDefaultProps) {
    const { post, relatedEntities, locale } = useArticleData();

    return (
        <article className="min-h-screen">
            {/* Article Header with branding */}
            <SectionStrip brandedStyle={sectionStyles?.[ARTICLE_HERO]}>
                {breadcrumbs ? <div className="max-w-350 mx-auto px-4 pt-4">{breadcrumbs}</div> : null}
                <ArticleHeader variant="default" />
            </SectionStrip>

            {/* Content Section */}
            <div className="max-w-240 mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
                <AdSlot id="content-top" />
                {/* Related Matches - Before Body Content */}
                <RelatedMatches showHeader={false} />

                {/* Article Body Content */}
                <ArticleBody content={post.content} />

                {/* Match detail block — shown when post is tagged with a match */}
                <ArticleMatchDetail />

                {/* Authors */}
                <ArticleAuthors authors={post.populatedAuthors} />

                {/* Related Entities Section (Teams, Players, Matches) */}
                <RelatedEntities />

                {/* Challenge Zone */}
                <ChallengeZone relatedEntities={relatedEntities} locale={locale} />

                {/* Comments & Related Articles Section */}
                <SectionStrip brandedStyle={sectionStyles?.[ARTICLE_RELATED]}>
                    <DiscussionWidget entityId={`post-${post.id}`} discussionLabel="Comments" className="mt-8" />
                    <TabbedRelatedContent variant="standard" />
                </SectionStrip>

                <AdSlot id="content-bottom" />
            </div>
        </article>
    );
}
